import { useState, useMemo, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useLocation, useNavigate } from "react-router-dom";
import { base44 } from "@/api/base44Client";
import { useAuth } from "@/lib/AuthContext";
import {
  Dumbbell,
  Play,
  RotateCcw,
  Eye,
  Check,
  CalendarDays,
  Activity,
} from "lucide-react";
import { format } from "date-fns";
import { useActiveWorkout } from "@/lib/ActiveWorkoutContext";
import ExercisePreviewModal from "@/components/workouts/ExercisePreviewModal";
import { getWorkoutImage } from "@/utils/workoutImages";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

// Default fallback cinematic background image for workouts
const DEFAULT_WORKOUT_HERO_BG =
  "https://images.unsplash.com/photo-1517838277536-f5f99be501cd?w=1000&auto=format&fit=crop&q=80";

// Estimate duration in minutes
function estimateDurationLabel(exercises) {
  if (!exercises || !exercises.length) return "15 min";
  const secs = exercises.reduce((total, ex) => {
    const sets = Number(ex.sets) || 3;
    const rest = Number(ex.rest_seconds) || 60;
    return total + sets * 45 + (sets - 1) * rest + 30;
  }, 0);
  const min = Math.round(secs / 60);
  return `${Math.max(10, Math.round(min / 5) * 5)} min`;
}

const DAYS = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];

const GOAL_LABELS = {
  general_fitness: "General Fitness",
  strength: "Strength",
  hypertrophy: "Hypertrophy",
  endurance: "Endurance",
  fat_loss: "Fat Loss",
};

const BODY_PART_LABELS = {
  upper_body: "Upper Body",
  lower_body: "Lower Body",
  full_body: "Full Body",
  chest: "Chest",
  back: "Back",
  shoulders: "Shoulders",
  arms: "Arms",
  upper_arms: "Upper Arms",
  lower_arms: "Lower Arms",
  legs: "Legs",
  upper_legs: "Upper Legs",
  lower_legs: "Lower Legs",
  core: "Core",
  waist: "Waist",
  hips: "Hips",
  neck: "Neck",
  cardio: "Cardio",
};

function formatGoal(val) {
  if (!val) return "";
  const k = String(val).toLowerCase().trim();
  if (GOAL_LABELS[k]) return GOAL_LABELS[k];
  return k.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function formatBodyPart(val) {
  if (!val) return "";
  const k = String(val).toLowerCase().trim();
  if (BODY_PART_LABELS[k]) return BODY_PART_LABELS[k];
  return k.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

export default function TodaysWorkout() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const location = useLocation();
  const navigate = useNavigate();
  const { active: activeWorkout, startWorkout, resumeWorkout } =
    useActiveWorkout();

  const [previewOpen, setPreviewOpen] = useState(false);
  const [confirmRestartOpen, setConfirmRestartOpen] = useState(false);

  const todayName = DAYS[new Date().getDay()];
  const todayKey = todayName.toLowerCase();
  const todayDate = format(new Date(), "yyyy-MM-dd");

  // 1. Fetch user's weekly workout plan
  const { data: weeklyPlan = null, isLoading: weeklyLoading } = useQuery({
    queryKey: ["userWeeklyWorkoutPlan", user?.id],
    queryFn: () =>
      base44.entities.UserWeeklyWorkoutPlan.filter({ user_id: user.id }).then(
        (results) => results[0] || null
      ),
    enabled: !!user,
    staleTime: 5 * 60 * 1000,
  });

  const todayPlanId = weeklyPlan?.[todayKey];

  // 2. Fetch workout plans and templates
  const { data: workouts = [], isLoading: workoutsLoading } = useQuery({
    queryKey: ["workouts"],
    queryFn: () => base44.entities.WorkoutPlan.list(),
    staleTime: 5 * 60 * 1000,
  });

  const { data: templates = [], isLoading: templatesLoading } = useQuery({
    queryKey: ["workoutTemplates"],
    queryFn: () => base44.entities.WorkoutTemplate.list(),
  });

  const { data: allExercises = [] } = useQuery({
    queryKey: ["exercises"],
    queryFn: () => base44.entities.Exercises.list("name", 2500),
  });

  const { data: todaysWorkoutSessions = [] } = useQuery({
    queryKey: ["workout-sessions", todayDate, user?.id],
    queryFn: () =>
      base44.entities.WorkoutSession.filter({
        user_id: user.id,
        completed_date: todayDate,
      }),
    enabled: !!user,
  });

  const { data: completions = [] } = useQuery({
    queryKey: ["workout-completions", todayDate],
    queryFn: () =>
      base44.entities.WorkoutCompletion.filter({
        user_id: user.id,
        completed_date: todayDate,
      }),
    enabled: !!user,
  });

  // Active plan for today
  const activePlan = useMemo(() => {
    if (!todayPlanId) return null;
    const fromWorkouts = workouts.find(
      (w) =>
        w.id === todayPlanId ||
        w._id === todayPlanId ||
        String(w.id || w._id) === String(todayPlanId)
    );
    if (fromWorkouts) return fromWorkouts;
    const fromTemplates = templates.find(
      (t) =>
        t.id === todayPlanId ||
        t._id === todayPlanId ||
        String(t.id || t._id) === String(todayPlanId)
    );
    if (fromTemplates) {
      return {
        id: fromTemplates.id || fromTemplates._id,
        name: fromTemplates.name,
        exercises: fromTemplates.exercises || [],
        body_part_category: fromTemplates.body_part_category,
        goal: fromTemplates.goal,
      };
    }
    return null;
  }, [todayPlanId, workouts, templates]);

  const hasAnyScheduledWorkout = useMemo(() => {
    if (!weeklyPlan) return false;
    return [
      "monday",
      "tuesday",
      "wednesday",
      "thursday",
      "friday",
      "saturday",
      "sunday",
    ].some((d) => !!weeklyPlan[d]);
  }, [weeklyPlan]);

  const nextWorkoutInfo = useMemo(() => {
    if (!weeklyPlan) return null;
    const dayKeys = [
      "sunday",
      "monday",
      "tuesday",
      "wednesday",
      "thursday",
      "friday",
      "saturday",
    ];
    const currentIdx = new Date().getDay();
    for (let offset = 1; offset <= 6; offset++) {
      const dKey = dayKeys[(currentIdx + offset) % 7];
      const pId = weeklyPlan[dKey];
      if (pId) {
        const p =
          workouts.find((w) => String(w.id || w._id) === String(pId)) ||
          templates.find((t) => String(t.id || t._id) === String(pId));
        const dayLabel =
          offset === 1
            ? "Tomorrow"
            : dKey.charAt(0).toUpperCase() + dKey.slice(1);
        return {
          dayKey: dKey,
          dayLabel,
          planName: p?.name || "Scheduled Workout",
          exerciseCount: (p?.exercises || []).length,
        };
      }
    }
    return null;
  }, [weeklyPlan, workouts, templates]);

  const todaysExercises = activePlan ? activePlan.exercises || [] : [];
  const durationLabel = estimateDurationLabel(todaysExercises);

  // Check if session is active or already completed
  const isThisWorkoutActive = Boolean(
    activeWorkout &&
      (activeWorkout.plan_id === activePlan?.id ||
        activeWorkout.plan_name === activePlan?.name)
  );

  const previousSessionsForPlan = todaysWorkoutSessions.filter(
    (s) => s.workout_plan_id === activePlan?.id
  );
  const hasPriorSession =
    previousSessionsForPlan.length > 0 && !isThisWorkoutActive;

  const isCompleted = completions.some(
    (c) => c.workout_plan_id === activePlan?.id
  );

  // Background image resolution
  const heroImageUrl = useMemo(() => {
    return getWorkoutImage(activePlan);
  }, [activePlan]);

  // Handler for Start or Resume Workout
  const handleStartOrResume = () => {
    if (!activePlan) return;
    if (isThisWorkoutActive) {
      resumeWorkout();
    } else {
      startWorkout({
        ...activePlan,
        plan_id: activePlan.id,
        plan_name: activePlan.name,
        exercises: todaysExercises,
        total_exercises: todaysExercises.length,
      });
    }
    setPreviewOpen(false);
  };

  const handleRestart = async () => {
    for (const session of previousSessionsForPlan) {
      try {
        await base44.entities.WorkoutSession.delete(session.id);
      } catch (_e) {}
    }
    queryClient.invalidateQueries({ queryKey: ["workout-sessions"] });
    setConfirmRestartOpen(false);
    handleStartOrResume();
  };

  // 1. Loading State
  if (weeklyLoading || workoutsLoading || templatesLoading) {
    return (
      <div
        className="h-[290px] rounded-3xl bg-[#080F1D] border border-white/10 animate-pulse flex flex-col justify-end p-6 space-y-3"
        data-testid="todays-workout-loading"
      >
        <div className="h-4 bg-white/10 rounded w-28" />
        <div className="h-8 bg-white/20 rounded w-48" />
        <div className="h-4 bg-white/10 rounded w-36" />
        <div className="flex gap-3 pt-2">
          <div className="h-11 bg-white/10 rounded-xl w-28" />
          <div className="h-11 bg-sky-500/20 rounded-xl flex-1" />
        </div>
      </div>
    );
  }

  // 2. Rest Day State
  if (!activePlan && hasAnyScheduledWorkout) {
    return (
      <div
        className="h-[290px] rounded-3xl overflow-hidden relative p-6 flex flex-col justify-between shadow-xl"
        style={{
          background:
            "radial-gradient(ellipse at top right, #131E36 0%, #080F1D 100%)",
          border: "1px solid rgba(255, 255, 255, 0.1)",
        }}
        data-testid="todays-workout-rest-day"
      >
        <div className="flex items-center justify-between">
          <span className="text-[10px] sm:text-[11px] font-bold uppercase tracking-widest text-indigo-400 flex items-center gap-1.5">
            <Activity className="w-3.5 h-3.5" /> TODAY'S TRAINING
          </span>
          <span className="px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-indigo-500/20 text-indigo-300 border border-indigo-500/40">
            REST & RECOVERY
          </span>
        </div>

        <div className="space-y-1.5 my-auto">
          <h2 className="text-2xl sm:text-3xl font-display font-black text-white uppercase tracking-tight">
            Rest & Recovery Day
          </h2>
          <p className="text-xs sm:text-sm text-slate-300 max-w-md line-clamp-2">
            Muscles rebuild and grow stronger during recovery. Stay hydrated, prioritize protein, and relax.
          </p>
          {nextWorkoutInfo && (
            <div className="flex items-center gap-2 pt-1 text-xs text-sky-300">
              <CalendarDays className="w-3.5 h-3.5" />
              <span>
                Next up: <strong>{nextWorkoutInfo.planName}</strong> ({nextWorkoutInfo.dayLabel})
              </span>
            </div>
          )}
        </div>

        <div className="flex items-center gap-3 pt-2">
          <button
            type="button"
            onClick={() => navigate("/workouts")}
            className="flex-1 py-2.5 px-4 rounded-xl bg-white/10 hover:bg-white/15 border border-white/15 text-white font-bold text-xs sm:text-sm flex items-center justify-center gap-2 active:scale-95 transition"
          >
            <CalendarDays className="w-4 h-4 text-sky-400" /> View Schedule
          </button>
          <button
            type="button"
            onClick={() => navigate("/library")}
            className="flex-1 py-2.5 px-4 rounded-xl bg-sky-500/20 hover:bg-sky-500/30 border border-sky-400/40 text-sky-300 font-bold text-xs sm:text-sm flex items-center justify-center gap-2 active:scale-95 transition"
          >
            <Dumbbell className="w-4 h-4" /> Browse Library
          </button>
        </div>
      </div>
    );
  }

  // 3. No Plan Assigned State
  if (!activePlan && !hasAnyScheduledWorkout) {
    return (
      <div
        className="h-[290px] rounded-3xl overflow-hidden relative p-6 flex flex-col justify-between shadow-xl"
        style={{
          background:
            "radial-gradient(ellipse at top right, #101F38 0%, #080F1D 100%)",
          border: "1px solid rgba(255, 255, 255, 0.1)",
        }}
        data-testid="todays-workout-no-plan"
      >
        <div className="flex items-center justify-between">
          <span className="text-[10px] sm:text-[11px] font-bold uppercase tracking-widest text-sky-400 flex items-center gap-1.5">
            <Dumbbell className="w-3.5 h-3.5" /> TODAY'S TRAINING
          </span>
          <span className="px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-white/10 text-slate-300 border border-white/20">
            NO PLAN SET
          </span>
        </div>

        <div className="space-y-1.5 my-auto">
          <h2 className="text-2xl sm:text-3xl font-display font-black text-white uppercase tracking-tight">
            No Workout Scheduled
          </h2>
          <p className="text-xs sm:text-sm text-slate-300 max-w-md">
            Schedule a workout program for this week or select a workout from your library to start training.
          </p>
        </div>

        <div className="flex items-center gap-3 pt-2">
          <button
            type="button"
            onClick={() => navigate("/library")}
            className="flex-1 py-3 px-4 rounded-xl bg-gradient-to-r from-sky-500 to-blue-600 hover:from-sky-400 hover:to-blue-500 text-white font-extrabold text-xs sm:text-sm shadow-[0_0_18px_rgba(14,165,233,0.35)] flex items-center justify-center gap-2 active:scale-95 transition"
          >
            <Dumbbell className="w-4 h-4" /> Explore Workouts
          </button>
        </div>
      </div>
    );
  }

  // 4. APPROVED 290px CINEMATIC WORKOUT HERO
  const formattedGoal = formatGoal(activePlan?.goal);
  const formattedBodyPart = formatBodyPart(activePlan?.body_part_category);

  const workoutType = (
    formattedGoal ||
    formattedBodyPart ||
    "STRENGTH"
  ).toUpperCase();

  const muscleSubtitle =
    formattedBodyPart && formattedGoal && formattedBodyPart.toLowerCase() !== formattedGoal.toLowerCase()
      ? `${formattedBodyPart} · ${formattedGoal}`
      : formattedBodyPart ||
        (activePlan?.goal === "hypertrophy"
          ? "Hypertrophy & Growth"
          : formattedGoal || "Strength & Power");

  return (
    <>
      <div
        className="h-[290px] relative rounded-3xl overflow-hidden w-full select-none shadow-2xl border border-white/15 group"
        data-testid="todays-workout-card"
      >
        {/* Full-bleed background image with object-cover */}
        <img
          src={heroImageUrl}
          alt={activePlan?.name || "Today's Workout"}
          className="absolute inset-0 w-full h-full object-cover object-center filter brightness-[0.9] group-hover:scale-105 transition-transform duration-700"
        />

        {/* Gradient overlays: Strongest near bottom for typography readability */}
        <div className="absolute inset-0 bg-gradient-to-t from-[#080F1D] via-[#080F1D]/75 to-black/35 pointer-events-none" />
        <div className="absolute inset-x-0 top-0 h-24 bg-gradient-to-b from-black/70 to-transparent pointer-events-none" />

        {/* TOP OVERLAY: FRIDAY · STRENGTH | 02 EXERCISES */}
        <div className="absolute top-4 inset-x-4 sm:top-5 sm:inset-x-5 flex items-center justify-between z-10">
          <div className="px-3 py-1 rounded-full bg-black/40 backdrop-blur-md border border-white/15 text-[10px] sm:text-[11px] font-black uppercase tracking-wider text-sky-400">
            {todayName.toUpperCase()} · {workoutType}
          </div>
          <div className="px-3 py-1 rounded-full bg-black/40 backdrop-blur-md border border-white/15 text-[10px] sm:text-[11px] font-black uppercase tracking-wider text-slate-200">
            {String(todaysExercises.length).padStart(2, "0")} EXERCISES
          </div>
        </div>

        {/* BOTTOM OVERLAY: TODAY'S TRAINING, TITLE, SUBTITLE & BUTTONS */}
        <div className="absolute bottom-4 inset-x-4 sm:bottom-5 sm:inset-x-5 flex flex-col justify-end space-y-3 z-10">
          <div className="space-y-0.5">
            <p className="text-[10px] sm:text-[11px] font-black uppercase tracking-widest text-sky-400">
              TODAY'S TRAINING
            </p>
            <h2 className="text-2xl sm:text-3xl font-display font-black text-white uppercase tracking-tight drop-shadow-md truncate">
              {activePlan?.name || "TODAY'S WORKOUT"}
            </h2>
            <p className="text-xs sm:text-sm text-slate-300 font-medium">
              {muscleSubtitle} · {durationLabel}
            </p>
          </div>

          {/* OVERLAY BUTTONS: PREVIEW (GLASS) & START/RESUME (ELECTRIC-BLUE) */}
          <div className="flex items-center gap-2.5 pt-0.5">
            <button
              type="button"
              onClick={() => setPreviewOpen(true)}
              data-testid="workout-preview-btn"
              className="px-4 py-2.5 sm:py-3 rounded-xl bg-white/10 hover:bg-white/15 backdrop-blur-md border border-white/20 text-white font-bold text-xs sm:text-sm tracking-wide flex items-center justify-center gap-1.5 active:scale-95 transition-all shadow-sm"
            >
              <Eye className="w-4 h-4 text-sky-300" />
              <span>PREVIEW</span>
            </button>

            {hasPriorSession ? (
              <button
                type="button"
                onClick={() => setConfirmRestartOpen(true)}
                data-testid="workout-restart-btn"
                className="flex-1 py-2.5 sm:py-3 px-4 rounded-xl bg-amber-500 hover:bg-amber-400 text-white font-black text-xs sm:text-sm tracking-wider uppercase shadow-[0_0_20px_rgba(245,158,11,0.4)] flex items-center justify-center gap-2 active:scale-95 transition-all"
              >
                <RotateCcw className="w-4 h-4" />
                <span>RESTART WORKOUT</span>
              </button>
            ) : isThisWorkoutActive ? (
              <button
                type="button"
                onClick={handleStartOrResume}
                data-testid="workout-resume-btn"
                className="flex-1 py-2.5 sm:py-3 px-4 rounded-xl bg-gradient-to-r from-sky-500 to-blue-600 hover:from-sky-400 hover:to-blue-500 text-white font-black text-xs sm:text-sm tracking-wider uppercase shadow-[0_0_20px_rgba(14,165,233,0.45)] flex items-center justify-center gap-2 active:scale-95 transition-all"
              >
                <RotateCcw className="w-4 h-4" />
                <span>RESUME WORKOUT</span>
              </button>
            ) : isCompleted ? (
              <button
                type="button"
                onClick={() => setConfirmRestartOpen(true)}
                data-testid="workout-completed-btn"
                className="flex-1 py-2.5 sm:py-3 px-4 rounded-xl bg-emerald-500 hover:bg-emerald-400 text-white font-black text-xs sm:text-sm tracking-wider uppercase shadow-[0_0_20px_rgba(16,185,129,0.35)] flex items-center justify-center gap-2 active:scale-95 transition-all"
              >
                <Check className="w-4 h-4 stroke-[3]" />
                <span>COMPLETED (TAP TO REPEAT)</span>
              </button>
            ) : (
              <button
                type="button"
                onClick={handleStartOrResume}
                data-testid="workout-start-btn"
                className="flex-1 py-2.5 sm:py-3 px-4 rounded-xl bg-gradient-to-r from-sky-500 to-blue-600 hover:from-sky-400 hover:to-blue-500 text-white font-black text-xs sm:text-sm tracking-wider uppercase shadow-[0_0_20px_rgba(14,165,233,0.45)] flex items-center justify-center gap-2 active:scale-95 transition-all"
              >
                <Play className="w-4 h-4 fill-white text-white" />
                <span>START WORKOUT</span>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* WORKOUT PREVIEW MODAL — ALWAYS-VISIBLE INSTRUCTIONS, EXERCISES, SETS/REPS, REST */}
      <ExercisePreviewModal
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        exercises={todaysExercises}
        initialIndex={0}
        allExercises={allExercises}
        workoutName={activePlan?.name || "Workout"}
        onStartWorkout={handleStartOrResume}
      />

      {/* CONFIRM RESTART DIALOG */}
      <AlertDialog
        open={confirmRestartOpen}
        onOpenChange={setConfirmRestartOpen}
      >
        <AlertDialogContent className="bg-[#0A1220] border-white/10 text-white">
          <AlertDialogHeader>
            <AlertDialogTitle>Restart this workout?</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-400 text-xs sm:text-sm">
              You already logged a session for today. Restarting will clear
              today's active session data for this workout and start fresh.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-white/10 text-white hover:bg-white/20 border-white/10">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleRestart}
              className="bg-amber-500 hover:bg-amber-400 text-white font-bold"
            >
              Restart
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}